import React, { useState } from "react";
import { KTSVG } from "../../../helpers";

type Props = {
  className: string;
  innerPadding?: string;
};

const StepContent12: React.FC<Props> = ({ className, innerPadding = "" }) => {
  const [category, setCategory] = useState("1");
  const [option, setOption] = useState("1");
  const [fleet, setFleet] = useState("1");
  return (

      <div className={`card ${className}`}>
        {/* begin::Body */}
        <div className={`card-body ${innerPadding}`}>
          {/* begin::Title */}
          <div className="mb-10">
            <h3 className="fw-bold text-dark mb-2 fs-3">Determine decision-making unit (DMU)</h3>
            <span className="fs-7 text-muted">
             Read each section and then fill in your answers at the bottom.
            </span>
          </div>
          {/* end::Title */}

          {/* begin::Section */}
          <div className="mb-10">
            <div className="accordion" id="kt_accordion_1">
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_1">
                  <button
                    className="accordion-button fs-7 fw-bold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_1"
                    aria-expanded="true"
                    aria-controls="kt_accordion_1_body_1"
                  >
                    Determine decision-making unit (DMU)
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_1"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_1"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  In this step you will research and document who are the key people or sources of information that ultimately determines whether or not your target customer will buy your product. There will be a wide array of people involved in the decision to use your product. Some people may play dual roles within their company. Not all will have the same viewpoint or understanding of the situation.

Critical to the decision making process are the end user, the economic buyer, and the advocate. Others, such as those who provide information about their company's pain points and solution needs, will have varying levels of influence over the decision to buy. Your target customer almost surely has a decision-making group of more than one person. Understanding this group and explicitly mapping out each person’s role and interest is of critical importance not just for the sale, but also much earlier in the process when you are developing the product and all of its attributes.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_2">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_2"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_2"
                  >
                    Identify the primary stakeholders in the DMU
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_2"
                  className="accordion-collapse collapse show"
                  aria-labelledby="kt_accordion_1_header_2"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  To sell your product successfully, you will need to identify all persons who will be involved in the decision to acquire the product for the end user.

Some people will actively approve or block acquisition, while others will present opinions that sway decisions. This process holds true for both B2B and B2C, though B2C may involve fewer people, each of whom could have multiple roles.

Identify the champion: The champion is the person who wants the customer to purchase the product; this person, may or may not be your end user. Usually there is only one primary champion, but multiple people can advocate for your offering, and provide support to the primary champion. Find that person in a few cases and then build a profile. Though it can vary from situation to situation, understand what the most-common elements are in the profile of champions. Champions can also be referred to as “advocates”.
Identify end users: These are the persons who will actually use the product to create the value that is described in Step 8, Quantified Value Proposition.
Identify the primary economic buyer: This is the person from whose budget the money will come to pay for your product. They have the ultimate sign off to release the funds. Often, this is the final approval and everyone else will defer to sign off on spending money to purchase your product.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_3">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_3"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_3"
                  >
                    Identify the secondary stakeholders in the DMU
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_3"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_3"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  Identify Primary and Secondary Influencers. These individuals often have a depth of experience in the subject matter, and can influence the rest of the DMU, including the champion and end user. Primary may be consultants, engineering firms, friends and family, industry analysts, journalists. Secondary could likely include media publications, product review sites, industry groups, websites, blogs, and anyone else who the primary stakeholders turns to for information and feedback.
Identify who has veto power. These individuals have the ability to reject a purchase for any reason. In a B2B environment, this individual can outrank the advocate or end user. It could be a person who enforces standards in the company or a regulatory officer. The person with veto power is less common in a B2C environment.
Identify the purchasing department. This department handles the logistics of the purchase. They can be another obstacle, as this department often looks to drive prices down, even after the decision to purchase has been made by the primary economic buyer. They can try to disqualify you based on certain purchasing rules that the company has set. In general, they are a link in the chain that you should neutralize, but not sell to.
                  </div>
                </div>
              </div>
              <div className="accordion-item">
                <h2 className="accordion-header" id="kt_accordion_1_header_4">
                  <button
                    className="accordion-button fs-7 fw-bold collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#kt_accordion_1_body_4"
                    aria-expanded="false"
                    aria-controls="kt_accordion_1_body_4"
                  >
                    Create a visual map of the DMU
                  </button>
                </h2>
                <div
                  id="kt_accordion_1_body_4"
                  className="accordion-collapse collapse"
                  aria-labelledby="kt_accordion_1_header_4"
                  data-bs-parent="#kt_accordion_1"
                >
                  <div className="accordion-body">
                  Once you have identified all the above stakeholders, plot the information visually so it is unambiguous (see example below).

A visual map of the DMU
While this is a good example, it leaves off the influencers who are more general like websites, blogs, industry analysts and the like that we mentioned above. You should include these as well.

You can then show this map to your Persona and Next 10 Customers to get feedback quickly, helping you revise the map until it accurately reflects the DMU for your first set of customers. The map also helps communicate the gathered information within your team. The DMU for each customer should be similar, and you should see patterns start to emerge. If you do not, either your customers do not match the Persona, or you have not segmented the market enough.

Create DMU map.
Get feedback on the map from Persona or Next 10 Customers.
Revise DMU map based on feedback.
                  </div>
                </div>
              </div>

            </div>
          </div>
          {/* end::Section */}

        </div>
        {/* end: Card Body */}
      </div>
      
  );
};

export { StepContent12 };
